export type CommonSenseQuestionType = "QUIZ" | "FAIR_PRIZE" | "TIMELINE"
export type CommonSenseDataKey = "quiz" | "fair_prize" | "timeline"

export const PRIMEVUE_PRIMARY_PALETTE_HEX = [
	"#10b981",
	"#22c55e",
	"#84cc16",
	"#f97316",
	"#f59e0b",
	"#eab308",
	"#14b8a6",
	"#06b6d4",
	"#0ea5e9",
	"#3b82f6",
	"#6366f1",
	"#8b5cf6",
	"#a855f7",
	"#d946ef",
	"#ec4899",
	"#f43f5e",
] as const

export const COMMON_SENSE_FONT_OPTIONS = [
	{ label: "Inter", value: "Inter, sans-serif" },
	{ label: "Roboto", value: "Roboto, sans-serif" },
	{ label: "Montserrat", value: "Montserrat, sans-serif" },
	{ label: "Playfair Display", value: "'Playfair Display', serif" },
	{ label: "Oswald", value: "Oswald, sans-serif" },
] as const

export function resolveCommonSenseFontFamily(raw: unknown): string {
	if (typeof raw === "string" && raw.trim()) return raw.trim()
	return COMMON_SENSE_FONT_OPTIONS[0].value
}

export const COMMON_SENSE_DEFAULT_TIME_PHASE = 30

/** Matches CommonSense service TIMELINE_RULES / FAIR_PRIZE_RULES / QUIZ_RULES `time_results`. */
export const COMMON_SENSE_DEFAULT_TIME_RESULTS: Record<CommonSenseQuestionType, number> = {
	QUIZ: 20,
	FAIR_PRIZE: 20,
	TIMELINE: 30,
}

export function defaultCommonSenseTimeResults(type: unknown): number {
	const raw = String(type ?? "QUIZ")
		.trim()
		.toUpperCase()
		.replace(/-/g, "_")
	if (raw === "TIMELINE") return COMMON_SENSE_DEFAULT_TIME_RESULTS.TIMELINE
	if (raw === "FAIR_PRIZE") return COMMON_SENSE_DEFAULT_TIME_RESULTS.FAIR_PRIZE
	return COMMON_SENSE_DEFAULT_TIME_RESULTS.QUIZ
}

/** Persist a positive value, or the type default (never leave null/0). */
export function resolveCommonSenseTimeResults(type: unknown, value: unknown): number {
	if (value != null && value !== "") {
		const parsed = Number(value)
		// Legacy CMS/customization form default was 15 — map to type defaults.
		if (Number.isFinite(parsed) && parsed > 0 && parsed !== 15) {
			return Math.round(parsed)
		}
	}
	return defaultCommonSenseTimeResults(type)
}

export const COMMON_SENSE_MAX_ANSWERS = 6
export const COMMON_SENSE_MAX_TIMELINE_STEPS = 6

export const COMMON_SENSE_TYPE_TO_DATA_KEY: Record<CommonSenseQuestionType, CommonSenseDataKey> = {
	QUIZ: "quiz",
	FAIR_PRIZE: "fair_prize",
	TIMELINE: "timeline",
}

export const COMMON_SENSE_DATA_KEYS: CommonSenseDataKey[] = ["quiz", "fair_prize", "timeline"]

export function createCommonSenseUuid(): string {
	if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
		return crypto.randomUUID()
	}
	return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
		const random = Math.floor(Math.random() * 16)
		const value = char === "x" ? random : (random & 0x3) | 0x8
		return value.toString(16)
	})
}

export function commonSenseDataKeyForType(type: unknown): CommonSenseDataKey {
	const raw = String(type ?? "QUIZ")
		.trim()
		.toUpperCase()
		.replace(/-/g, "_")

	if (raw === "TIMELINE") return "timeline"
	if (raw === "FAIR_PRIZE") return "fair_prize"
	return "quiz"
}

export function commonSenseTypeForDataKey(key: CommonSenseDataKey): CommonSenseQuestionType {
	if (key === "timeline") return "TIMELINE"
	if (key === "fair_prize") return "FAIR_PRIZE"
	return "QUIZ"
}

export function getCommonSenseQuestionArray(data: unknown, type: unknown): Record<string, unknown>[] {
	if (Array.isArray(data)) return data.filter((item): item is Record<string, unknown> => Boolean(item && typeof item === "object"))
	if (!data || typeof data !== "object") return []
	const record = data as Record<string, unknown>
	const preferredKey = commonSenseDataKeyForType(type)
	const value = record[preferredKey]
	if (Array.isArray(value)) {
		return value.filter((item): item is Record<string, unknown> => Boolean(item && typeof item === "object"))
	}
	return []
}

export function setCommonSenseQuestionArray(data: unknown, type: unknown, items: Record<string, unknown>[]) {
	const key = commonSenseDataKeyForType(type)
	return { [key]: items }
}

export function createEmptyCommonSenseData(type: unknown) {
	return setCommonSenseQuestionArray({}, type, [])
}

const CHALLENGE_DATA_LANG_FALLBACK_ORDER = ["es", "en", "lang1", "lang2", "lang3"] as const
const TIMELINE_ORDER_CONTEXT_FIELDS = ["order_context_start", "order_context_end"] as const

export function challengeDataLangFallbackKeys(preferred: string | null): string[] {
	const ordered: string[] = []
	if (preferred) ordered.push(preferred)
	for (const key of CHALLENGE_DATA_LANG_FALLBACK_ORDER) {
		if (!ordered.includes(key)) ordered.push(key)
	}
	return ordered
}

export function hasCommonSenseChallengeSteps(data: unknown, type: unknown): boolean {
	return getCommonSenseQuestionArray(data, type).length > 0
}

/**
 * When timeline order hints are empty in the event-language payload, copy them from other
 * `data_*` blobs (same round index). Does not override question text or step labels.
 */
export function mergeTimelineOrderContextFromLanguageFallbacks(
	primary: unknown,
	fallbacks: unknown[],
	type: unknown
): unknown {
	if (commonSenseDataKeyForType(type) !== "timeline") return primary
	const items = getCommonSenseQuestionArray(primary, type)
	if (!items.length || !fallbacks.length) return primary

	const merged = items.map((item, index) => {
		const row = { ...item }
		for (const field of TIMELINE_ORDER_CONTEXT_FIELDS) {
			if (String(row[field] ?? "").trim()) continue
			for (const fb of fallbacks) {
				const fbItem = getCommonSenseQuestionArray(fb, type)[index]
				const val = fbItem?.[field]
				if (String(val ?? "").trim()) {
					row[field] = val
					break
				}
			}
		}
		return row
	})
	return setCommonSenseQuestionArray(primary, type, merged)
}

/** Vue router path pattern for the client customization page. */
export const COMMON_SENSE_CUSTOMIZE_ROUTE = "/common-sense/customize/:custom_uuid"

export function buildCommonSenseCustomizePath(customUuid: string) {
	return `/common-sense/customize/${customUuid}`
}

export function buildCommonSenseCustomizeUrl(
	customUuid: string,
	origin: string = typeof window !== "undefined" ? window.location.origin : ""
) {
	return `${origin}${buildCommonSenseCustomizePath(customUuid)}`
}
